import {useCallback, useEffect, useState} from 'react'
import type {ScrollParams} from 'react-virtualized/dist/es/Grid'

export function useSidebar(collapsed: boolean, toggleCollapseSidebar: () => void) {
  const [sidedarCollapsedWithActiveSearch, setSidedarCollapsedWithActiveSearch] =
    useState<boolean>()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchActive, setSearchActive] = useState(false)

  const changeSidebarSearchQuery = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const changeSidebarSearchActive = useCallback(
    (active: boolean) => {
      setSearchActive(active)

      if (active && collapsed) {
        setSidedarCollapsedWithActiveSearch(true)
        toggleCollapseSidebar()
      }
      if (!active && !collapsed && sidedarCollapsedWithActiveSearch) {
        setSidedarCollapsedWithActiveSearch(false)
        toggleCollapseSidebar()
      }

      if (!active) {
        setSearchQuery('')
      }
    },
    [collapsed, sidedarCollapsedWithActiveSearch, toggleCollapseSidebar],
  )

  const toggleCollapseSidebarAction = useCallback(() => {
    if (!sidedarCollapsedWithActiveSearch) {
      toggleCollapseSidebar()
    }
  }, [sidedarCollapsedWithActiveSearch, toggleCollapseSidebar])

  useEffect(() => {
    if (collapsed) {
      changeSidebarSearchActive(false)
    }
  }, [changeSidebarSearchActive, collapsed])

  return {
    searchQuery,
    searchActive,
    changeSidebarSearchQuery,
    changeSidebarSearchActive,
    toggleCollapseSidebarAction,
  }
}

type ScrollState = {
  isScrollAtStart: boolean
  isScrollAtEnd: boolean
}

export function useSidebarScroll(): [ScrollState | null, onScroll: (params: ScrollParams) => void] {
  const [state, setState] = useState<ScrollState | null>(null)

  const onScroll = useCallback(
    (params: ScrollParams) => {
      const isScrollAtStart = params.scrollTop === 0
      const isScrollAtEnd = params.scrollTop === params.scrollHeight - params.clientHeight

      if (isScrollAtStart !== state?.isScrollAtStart || isScrollAtEnd !== state?.isScrollAtEnd) {
        setState({isScrollAtStart, isScrollAtEnd})
      }
    },
    [state],
  )

  return [state, onScroll]
}
